import { getWebVitalSnapshotKey } from './metric-snapshots.js';

export const hasBrowserDocument = () => (
  typeof window !== 'undefined'
  && typeof document !== 'undefined'
  && window.document === document
  && document.nodeType === 9
  && typeof document.addEventListener === 'function'
  && typeof window.addEventListener === 'function'
  && typeof globalThis.performance?.getEntriesByType === 'function'
  && globalThis.navigator?.product !== 'ReactNative'
);

const subscribers = new Set();
// Retain only the latest instance of each of the five metrics. Raw entries,
// navigation URLs, attribution and DOM nodes never enter these snapshots.
const latest = new Map();
let initialization = null;
let started = false;

const deliver = (subscriber, snapshot) => {
  if (!subscribers.has(subscriber)) return;
  const context = {};
  try {
    const provided = subscriber.getContext?.() || {};
    if (typeof provided.route === 'string') context.route = subscriber.sanitizeRoute(provided.route);
    if (typeof provided.journey === 'string') context.journey = provided.journey.slice(0, 64);
  } catch {
    // A reporter's context provider must not break any other subscriber.
  }
  if (!subscribers.has(subscriber)) return;
  const sample = {
    ...snapshot,
    metadata: { ...snapshot.metadata },
    ...context,
  };
  try {
    subscriber.record(sample);
  } catch {
    // Analytics is best effort; isolate errors from application code.
    return;
  }
  if (!subscribers.has(subscriber)) return;
  try {
    subscriber.onReport?.(sample);
  } catch {
    // A flush callback cannot interrupt other reporters or official observers.
  }
};

const onMetric = (metric) => {
  const name = typeof metric?.name === 'string' ? metric.name.toLowerCase() : '';
  if (!Number.isFinite(metric?.value) || metric.value < 0) return;
  const previous = latest.get(name);
  const revision = previous?.metadata.id === metric.id ? previous.metadata.snapshotRevision + 1 : 1;
  const snapshot = {
    metric: name,
    value: metric.value,
    unit: name === 'cls' ? 'score' : 'ms',
    metadata: {
      collector: 'web-vitals',
      collectorVersion: '6.2.3',
      id: metric.id,
      snapshotRevision: revision,
      rating: metric.rating,
      delta: metric.delta,
      navigationType: metric.navigationType,
    },
  };
  if (!getWebVitalSnapshotKey(snapshot)) return;
  latest.set(name, snapshot);
  // Copy the set so subscribing during a callback cannot deliver twice.
  for (const subscriber of [...subscribers]) deliver(subscriber, snapshot);
};

const start = () => {
  if (started || initialization || !subscribers.size || !hasBrowserDocument()) return;
  initialization = import('web-vitals').then((vitals) => {
    initialization = null;
    // Disposal may happen while the browser-only chunk is loading.
    if (!subscribers.size || !hasBrowserDocument()) return;
    started = true;
    for (const name of ['onCLS', 'onINP', 'onLCP', 'onFCP', 'onTTFB']) {
      try {
        vitals[name](onMetric, { reportAllChanges: false, reportSoftNavs: false });
      } catch {
        // One unsupported metric must not prevent the other registrations.
      }
    }
  }).catch(() => {
    initialization = null;
    // A failed optional chunk cannot break the app. A later subscription retries.
  });
};

/**
 * One lazy official collector per module/document lifetime, shared by consumers.
 * Subscribe as early as possible; do not wait for load or idle. The official
 * library has no teardown API. Cleanup only removes this subscriber and never
 * synthesizes final values or disconnects another consumer's observers.
 */
export const subscribeWebVitals = ({ record, getContext, onReport, sanitizeRoute }) => {
  if (typeof record !== 'function' || !hasBrowserDocument()) return () => {};
  const subscriber = { record, getContext, onReport, sanitizeRoute };
  subscribers.add(subscriber);
  for (const snapshot of latest.values()) deliver(subscriber, snapshot);
  start();
  return () => { subscribers.delete(subscriber); };
};
